<template>
  <div v-if="isMaintenance" class="maintenance-wrapper">
    <Maintenance />
  </div>
  <div v-else :class="containerClass" @click="onWrapperClick">
    <AppTopBar
      :freeTimesCount="freeTimesCount"
      :freeTimesOpen="forceShowFreeTimes"
      @menu-toggle="onMenuToggle"
      @toggle-who-is-out="onToggleWhoIsOut"
      @toggle-free-times="forceShowFreeTimes = !forceShowFreeTimes"
    />
    <transition name="layout-sidebar">
      <div
        :class="sidebarClass"
        @click="onSidebarClick"
        v-show="isSidebarVisible()"
        v-if="this.$store.state.auth.status.loggedIn"
      >
        <div class="layout-logo">
          <router-link to="/">
            <img
              src="./assets/icon_w4m.png"
              alt="Logo"
              height="75px"
              width="75px"
            />
          </router-link>
        </div>
        <AppProfile />
        <!--
          One box for both: typing filters the menu; Enter opens the first
          page it finds, or searches the intranet when no page matches; the
          magnifier always searches the intranet. `searchInput` is the id
          the search page clears on arrival.
        -->
        <div class="search-input menu-search">
          <div class="p-inputgroup">
            <InputText
              placeholder="Pesquisar…"
              v-model="menuSearchTerm"
              id="searchInput"
              aria-label="Pesquisar páginas do menu ou registos da intranet"
              autocomplete="off"
              @keydown.enter.prevent="onMenuSearchEnter"
              @keydown.esc.prevent="clearMenuSearch"
            />
            <Button
              v-if="menuSearchActive"
              icon="pi pi-times"
              class="p-button-secondary p-button-outlined"
              aria-label="Limpar"
              v-tooltip.bottom="'Limpar'"
              @click="clearMenuSearch"
            />
            <Button
              icon="pi pi-search"
              class="p-button-warning"
              aria-label="Pesquisar na intranet"
              v-tooltip.bottom="'Pesquisar na intranet'"
              @click="goToSeach"
            />
          </div>
          <small v-if="menuSearchActive" class="menu-search-empty">
            <template v-if="displayedMenu.length">Enter abre a primeira página · a lupa pesquisa na intranet</template>
            <template v-else>Sem páginas com «{{ menuSearchTerm }}» · Enter pesquisa na intranet</template>
          </small>
        </div>
        <AppMenu
          :model="displayedMenu"
          :forceOpen="menuSearchActive"
          @menuitem-click="onMenuItemClick"
        />
      </div>
    </transition>
    <div
      v-bind:class="[
        { 'no-margin-left': !this.$store.state.auth.status.loggedIn },
        'layout-main',
      ]"
    >
      <!-- TEMP_INTERNAL_TABS_OFF_START -->
      <!--
      <OpenPagesBar
        v-if="this.$store.state.auth.status.loggedIn && openPageTabs.length > 0"
        :tabs="openPageTabs"
        :activeTabKey="activePageTabKey"
        @activate-tab="onActivateTab"
        @close-tab="onCloseTab"
      />
      -->
      <!-- TEMP_INTERNAL_TABS_OFF_END -->
      <Notifications
        :forceOpen="forceShowFreeTimes"
        @close="forceShowFreeTimes = false"
        @update-count="freeTimesCount = $event"
      />
      <HelpdeskQuickSearch
        ref="helpdeskQuickSearch"
        :show="showHelpdeskQuickSearch"
        @close="closeHelpdeskQuickSearch"
      />
      <keep-alive>
        <router-view v-if="shouldTrackRoute($route)" :key="getRouterViewKey($route)" />
      </keep-alive>
      <router-view v-if="!shouldTrackRoute($route)" :key="getRouterViewKey($route)" />
    </div>
    <AppFooter />
  </div>
</template>

<script>
import AppTopBar from "./AppTopbar.vue";
import AppProfile from "./AppProfile.vue";
import AppMenu from "./AppMenu.vue";
import AppFooter from "./AppFooter.vue";
import Notifications from "@/components/Notifications.vue";
import { requestWhoIsOut } from "@/helpers/whoIsOutBus";
import Maintenance from "@/views/Maintenance.vue";
// TEMP_INTERNAL_TABS_OFF_START
// import OpenPagesBar from "./components/navigation/OpenPagesBar.vue";
// TEMP_INTERNAL_TABS_OFF_END
import HelpdeskQuickSearch from "@/components/helpdeskTickets/HelpdeskQuickSearch.vue";
import { registerTabHost, unregisterTabHost } from "@/helpers/appTabNavigation";
import { buildMenu } from "@/config/menu";
import {
  loadBudgetingPermissions,
  resetBudgetingPermissions,
} from "@/modules/budgeting/permissions";
import { filterMenu, firstLeaf } from "@/helpers/menuSearch";

export default {
  data() {
    return {
      menuSearchTerm: "",
      layoutMode: "static",
      layoutColorMode: "dark",
      staticMenuInactive: !this.$store.state.auth.status.loggedIn,
      overlayMenuActive: false,
      mobileMenuActive: !this.$isMobile(),
      openPageTabs: [],
      activePageTabKey: null,
      menuPathLabelMap: {},
      isInternalTabsTemporarilyDisabled: true,
      showHelpdeskQuickSearch: false,
      forceShowFreeTimes: false,
      /** Technicians without bookings not yet marked read (top bar badge). */
      freeTimesCount: 0,
      menu: buildMenu(this),
    };
  },
  methods: {
    shouldTrackRoute(route) {
      // TEMP_INTERNAL_TABS_OFF_START
      if (this.isInternalTabsTemporarilyDisabled) {
        return false;
      }
      // TEMP_INTERNAL_TABS_OFF_END

      if (!route || !this.$store.state.auth.status.loggedIn) {
        return false;
      }

      const path = route.path || "";
      if (path === "/login" || path === "/forbidden" || path.indexOf("/2fa") === 0) {
        return false;
      }

      return true;
    },
    getOpenTabsStorageKey() {
      const user = this.$store.state.auth.user;
      if (!user) {
        return null;
      }

      const identifier = user.id || user.username || user.name || "default";
      return `openPagesTabs:${identifier}`;
    },
    serializeForTabKey(value) {
      if (Array.isArray(value)) {
        return `[${value.map((item) => this.serializeForTabKey(item)).join(",")}]`;
      }

      if (value !== null && typeof value === "object") {
        const keys = Object.keys(value).sort();
        const objectValues = keys.map(
          (key) => `${key}:${this.serializeForTabKey(value[key])}`
        );
        return `{${objectValues.join(",")}}`;
      }

      return JSON.stringify(value);
    },
    buildTabKey(route) {
      const path = route.path || "";
      const params = route.params || {};
      const query = route.query || {};
      return `${path}|p:${this.serializeForTabKey(params)}|q:${this.serializeForTabKey(query)}`;
    },
    getRouterViewKey(route) {
      if (!route) {
        return "";
      }

      const path = route.path || "";
      const params = route.params || {};
      const query = { ...(route.query || {}) };

      if (
        path.indexOf("/operational-management/entities/") === 0 ||
        path.indexOf("/operational-management/locals/") === 0
      ) {
        delete query.tab;
      }

      // The hubs keep their whole state in the query — period, scope, the KPI
      // slices — and rewrites it on every click. Keying the view on that
      // would tear the page down and rebuild it each time, dropping the
      // column filters and re-issuing every request. The view reads the
      // query itself, so the path alone identifies it.
      // The audit log mirrors its filters and open record in the query the
      // same way.
      if (
        path === "/commercial-hub" ||
        path === "/operations-hub" ||
        path === "/administration/audit-log" ||
        path === "/financial-hub" ||
        path === "/business-classification" ||
        path === "/approvals" ||
        path === "/quotes"
      ) {
        return `${path}|p:${this.serializeForTabKey(params)}`;
      }

      return `${path}|p:${this.serializeForTabKey(params)}|q:${this.serializeForTabKey(query)}`;
    },
    normalizeTabText(value) {
      if (!value) {
        return "Página";
      }

      return String(value)
        .replace(/[-_/]+/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/\b\w/g, (char) => char.toUpperCase());
    },
    getRouteIdentifierSuffix(route) {
      const params = route.params || {};
      const query = route.query || {};
      const paramKeys = Object.keys(params).filter((key) => {
        const value = params[key];
        return value !== undefined && value !== null && String(value).trim() !== "";
      });

      if (paramKeys.length > 0) {
        const priorityKey =
          paramKeys.find((key) => /(^id$|id$|_id$)/i.test(key)) || paramKeys[0];
        const paramValue = params[priorityKey];

        if (/^id$/i.test(priorityKey)) {
          return ` #${paramValue}`;
        }

        return ` (${priorityKey}: ${paramValue})`;
      }

      const queryKeys = Object.keys(query).filter((key) => {
        const value = query[key];
        return value !== undefined && value !== null && String(value).trim() !== "";
      });

      if (queryKeys.length > 0) {
        const queryKey = queryKeys[0];
        return ` (${queryKey}: ${query[queryKey]})`;
      }

      return "";
    },
    buildMenuPathLabelMap() {
      const labels = {};

      const visitItems = (items, parentLabel) => {
        (items || []).forEach((item) => {
          if (!item) {
            return;
          }

          const currentLabel = item.label || parentLabel;
          const path = item.to || item.url;
          const isInternalPath =
            typeof path === "string" &&
            path.indexOf("/") === 0 &&
            (!item.target || item.target === "_self");

          if (isInternalPath && currentLabel) {
            labels[path] = currentLabel;
          }

          if (Array.isArray(item.items) && item.items.length > 0) {
            visitItems(item.items, currentLabel);
          }
        });
      };

      visitItems(this.menu, null);
      this.menuPathLabelMap = labels;
    },
    resolveTabLabel(route) {
      if (!route) {
        return "W4M|Intranet";
      }

      const suffix = this.getRouteIdentifierSuffix(route);

      if (route.meta && route.meta.tabLabel) {
        return `${route.meta.tabLabel}${suffix}`;
      }

      const menuLabel = this.menuPathLabelMap[route.path];
      if (menuLabel) {
        return `${menuLabel}${suffix}`;
      }

      if (route.path === "/") {
        return `Home${suffix}`;
      }

      const fallbackSource = route.name || route.path;
      return `${this.normalizeTabText(fallbackSource)}${suffix}`;
    },
    updateBrowserPageTitle(route = this.$route) {
      if (Object.keys(this.menuPathLabelMap).length === 0) {
        this.buildMenuPathLabelMap();
      }

      if (route && route.meta && route.meta.pageTitle) {
        document.title = route.meta.pageTitle;
        return;
      }

      const pageLabel = this.resolveTabLabel(route);
      document.title = `${pageLabel} | W4M`;
    },
    buildTabFromRoute(route) {
      return {
        key: this.buildTabKey(route),
        path: route.path,
        query: { ...(route.query || {}) },
        params: { ...(route.params || {}) },
        hash: route.hash || "",
        label: this.resolveTabLabel(route),
      };
    },
    upsertTab(route) {
      if (Object.keys(this.menuPathLabelMap).length === 0) {
        this.buildMenuPathLabelMap();
      }

      const tab = this.buildTabFromRoute(route);
      const existingTabIndex = this.openPageTabs.findIndex((item) => item.key === tab.key);

      if (existingTabIndex === -1) {
        this.openPageTabs.push(tab);
        return tab;
      }

      const mergedTab = {
        ...this.openPageTabs[existingTabIndex],
        ...tab,
      };
      this.openPageTabs.splice(existingTabIndex, 1, mergedTab);
      return this.openPageTabs[existingTabIndex];
    },
    syncRouteTab(route = this.$route) {
      if (!this.shouldTrackRoute(route)) {
        return;
      }

      const tab = this.upsertTab(route);
      this.activePageTabKey = tab.key;
      this.persistOpenTabs();
    },
    onActivateTab(tabKey) {
      const tab = this.openPageTabs.find((item) => item.key === tabKey);
      if (!tab) {
        return;
      }

      this.activePageTabKey = tab.key;
      this.persistOpenTabs();
      this.$router
        .push({ path: tab.path, query: tab.query || {}, hash: tab.hash || "" })
        .catch(() => {});
    },
    onCloseTab(tabKey) {
      const tabIndex = this.openPageTabs.findIndex((item) => item.key === tabKey);
      if (tabIndex === -1) {
        return;
      }

      const wasActive = this.activePageTabKey === tabKey;
      this.openPageTabs.splice(tabIndex, 1);

      if (!wasActive) {
        this.persistOpenTabs();
        return;
      }

      const fallbackTab = this.openPageTabs[tabIndex] || this.openPageTabs[tabIndex - 1];
      if (fallbackTab) {
        this.onActivateTab(fallbackTab.key);
        return;
      }

      this.activePageTabKey = null;
      this.persistOpenTabs();
      this.$router.push("/").catch(() => {});
    },
    persistOpenTabs() {
      const storageKey = this.getOpenTabsStorageKey();
      if (!storageKey) {
        return;
      }

      localStorage.setItem(
        storageKey,
        JSON.stringify({
          tabs: this.openPageTabs,
          activeTabKey: this.activePageTabKey,
        })
      );
    },
    restoreOpenTabs() {
      const storageKey = this.getOpenTabsStorageKey();
      if (!storageKey) {
        return;
      }

      let parsedState = null;
      try {
        parsedState = JSON.parse(localStorage.getItem(storageKey));
      } catch (error) {
        localStorage.removeItem(storageKey);
      }

      if (!parsedState || !Array.isArray(parsedState.tabs)) {
        this.openPageTabs = [];
        this.activePageTabKey = null;
        return;
      }

      const restoredTabs = parsedState.tabs
        .filter((tab) => tab && typeof tab.key === "string" && typeof tab.path === "string")
        .map((tab) => ({
          key: tab.key,
          path: tab.path,
          query: tab.query || {},
          params: tab.params || {},
          hash: tab.hash || "",
          label: tab.label || this.normalizeTabText(tab.path),
        }))
        .filter((tab) => this.shouldTrackRoute({ path: tab.path }));

      this.openPageTabs = restoredTabs;
      this.activePageTabKey = restoredTabs.some((tab) => tab.key === parsedState.activeTabKey)
        ? parsedState.activeTabKey
        : restoredTabs[0]
          ? restoredTabs[0].key
          : null;
    },
    clearOpenTabsState() {
      this.openPageTabs = [];
      this.activePageTabKey = null;
      this.menuPathLabelMap = {};
    },
    /** The intranet-wide search for what is in the box; the menu goes back to whole. */
    goToSeach() {
      const term = this.menuSearchTerm.trim();
      this.$router.push(term ? `/search/${encodeURIComponent(term)}` : "/search/").catch(() => {});
      this.clearMenuSearch();
      this.overlayMenuActive = false;
      this.mobileMenuActive = false;
    },
    isEditableTarget(target) {
      if (!target) return false;
      if (target.isContentEditable) return true;
      const tagName = target.tagName ? target.tagName.toUpperCase() : "";
      if (["INPUT", "TEXTAREA", "SELECT"].includes(tagName)) return true;
      if (typeof target.closest === "function") {
        return !!target.closest("[contenteditable]:not([contenteditable='false'])");
      }
      return false;
    },
    onGlobalKeyDown(event) {
      const pressedKey = (event.key || "").toLowerCase();
      if (pressedKey !== "k" || (!event.ctrlKey && !event.metaKey)) return;
      if (!this.$store.state.auth.status.loggedIn) return;
      if (this.isEditableTarget(event.target)) return;
      // Ctrl/Cmd+Shift+K foca a pesquisa do menu; Ctrl/Cmd+K é do helpdesk.
      if (event.shiftKey) {
        event.preventDefault();
        const menuSearchInput = document.getElementById("searchInput");
        if (menuSearchInput) {
          menuSearchInput.focus();
        }
        return;
      }
      if (!this.isHelpdeskFullAccess) return;
      event.preventDefault();
      if (this.showHelpdeskQuickSearch) {
        if (this.$refs.helpdeskQuickSearch && this.$refs.helpdeskQuickSearch.focusSearchInput) {
          this.$refs.helpdeskQuickSearch.focusSearchInput();
        }
        return;
      }
      this.showHelpdeskQuickSearch = true;
      this.$nextTick(() => {
        if (this.$refs.helpdeskQuickSearch && this.$refs.helpdeskQuickSearch.focusSearchInput) {
          this.$refs.helpdeskQuickSearch.focusSearchInput();
        }
      });
    },
    closeHelpdeskQuickSearch() {
      this.showHelpdeskQuickSearch = false;
    },
    onWrapperClick() {
      if (!this.menuClick) {
        this.overlayMenuActive = false;
        this.mobileMenuActive = false;
      }
      this.menuClick = false;
    },
    onToggleWhoIsOut() {
      requestWhoIsOut();
      if (this.$route.path !== "/") {
        this.$router.push("/").catch(() => {});
      }
    },
    onMenuToggle() {
      this.menuClick = true;

      if (this.isDesktop()) {
        if (this.layoutMode === "overlay") {
          if (this.mobileMenuActive === true) {
            this.overlayMenuActive = true;
          }
          this.overlayMenuActive = !this.overlayMenuActive;
          this.mobileMenuActive = false;
        } else if (this.layoutMode === "static") {
          this.staticMenuInactive = !this.staticMenuInactive;
        }
      } else {
        this.mobileMenuActive = !this.mobileMenuActive;
      }
      event.preventDefault();
    },
    onSidebarClick() {
      this.menuClick = true;
    },
    clearMenuSearch() {
      this.menuSearchTerm = "";
    },
    onMenuSearchEnter() {
      const leaf = firstLeaf(this.displayedMenu);
      if (!leaf) {
        // No page by that name: it is a record to look for.
        this.goToSeach();
        return;
      }

      const destination = leaf.to || leaf.url;
      if (leaf.target === "_blank" || !/^\//.test(destination)) {
        window.open(destination, leaf.target || "_blank", "noopener");
      } else if (destination !== this.$route.fullPath) {
        this.$router.push(destination).catch(() => {});
      }

      this.clearMenuSearch();
      this.overlayMenuActive = false;
      this.mobileMenuActive = false;
    },
    onMenuItemClick(event) {
      if (event.item && !event.item.items) {
        this.overlayMenuActive = false;
        this.mobileMenuActive = false;
        // Escolhida uma página a partir da pesquisa, o menu volta ao normal.
        if (this.menuSearchActive) {
          this.clearMenuSearch();
        }
      }

      if (event && event.newTab === true && event.item && !event.item.items) {
        // TEMP_INTERNAL_TABS_OFF_START
        if (this.isInternalTabsTemporarilyDisabled) {
          return;
        }
        // TEMP_INTERNAL_TABS_OFF_END

        const resolved = this.$router.resolve(event.item.to || event.item.url);
        const route = resolved && resolved.route ? resolved.route : null;

        if (!this.shouldTrackRoute(route)) {
          return;
        }

        this.upsertTab(route);
        this.persistOpenTabs();
      }
    },

    onLayoutChange(layoutMode) {
      this.layoutMode = layoutMode;
    },
    onLayoutColorChange(layoutColorMode) {
      this.layoutColorMode = layoutColorMode;
    },
    addClass(element, className) {
      if (element.classList) element.classList.add(className);
      else element.className += " " + className;
    },
    removeClass(element, className) {
      if (element.classList) element.classList.remove(className);
      else
        element.className = element.className.replace(
          new RegExp(
            "(^|\\b)" + className.split(" ").join("|") + "(\\b|$)",
            "gi"
          ),
          " "
        );
    },
    isDesktop() {
      return window.innerWidth > 1024;
    },
    isSidebarVisible() {
      if (this.isDesktop()) {
        if (this.layoutMode === "static") return !this.staticMenuInactive;
        else if (this.layoutMode === "overlay") return this.overlayMenuActive;
        else return true;
      } else {
        return true;
      }
    },
  },
   computed: {
    isMaintenance() {
      return this.appConfig.maintenanceMode === 'true' || this.appConfig.maintenanceMode === true;
    },

     currentUser() {

      if (this.$store.state.auth.status.loggedIn) {
        return this.$store.state.auth.user;
      }
      return null;
    },
    isHelpdeskFullAccess() {
      const u = this.currentUser;
      return !!(u && this.can('seeAllTickets'));
    },
    menuSearchActive() {
      return this.menuSearchTerm.trim().length > 0;
    },
    // Com pesquisa activa, o menu é uma cópia podada aos ramos que casam; as
    // funções `visible` originais mantêm-se, por isso as ACLs continuam a mandar.
    displayedMenu() {
      return this.menuSearchActive
        ? filterMenu(this.menu, this.menuSearchTerm)
        : this.menu;
    },
    containerClass() {
      return [
        "layout-wrapper",
        {
          "layout-overlay": this.layoutMode === "overlay",
          "layout-static": this.layoutMode === "static",
          "layout-static-sidebar-inactive":
            this.staticMenuInactive && this.layoutMode === "static",
          "layout-overlay-sidebar-active":
            this.overlayMenuActive && this.layoutMode === "overlay",
          "layout-mobile-sidebar-active": this.mobileMenuActive,
          "p-input-filled": this.$appState.inputStyle === "filled",
          // 'p-ripple-disabled': this.$primevue.ripple === false
        },
      ];
    },
    sidebarClass() {
      return [
        "layout-sidebar",
        {
          "layout-sidebar-dark": this.layoutColorMode === "dark",
          "layout-sidebar-light": this.layoutColorMode === "light",
        },
      ];
    },
  },
  watch: {
    $route: {
      handler(route) {
        this.updateBrowserPageTitle(route);
        this.syncRouteTab(route);
      },
    },
    "$store.state.auth.status.loggedIn"(loggedIn) {
      if (!loggedIn) {
        resetBudgetingPermissions();
        this.clearOpenTabsState();
        this.updateBrowserPageTitle(this.$route);
        return;
      }

      // The budgeting module's permissions are not in user.moreInfo
      loadBudgetingPermissions();
      this.buildMenuPathLabelMap();
      this.updateBrowserPageTitle(this.$route);
      // TEMP_INTERNAL_TABS_OFF_START
      if (!this.isInternalTabsTemporarilyDisabled) {
        this.restoreOpenTabs();
        this.syncRouteTab(this.$route);
      }
      // TEMP_INTERNAL_TABS_OFF_END
    },
  },
  mounted() {
    // TEMP_INTERNAL_TABS_OFF_START
    if (!this.isInternalTabsTemporarilyDisabled) {
      registerTabHost(this);
    }
    // TEMP_INTERNAL_TABS_OFF_END

    if (!this.currentUser) {
      this.$router.push("/login").catch(() => {});
    }
    this.updateBrowserPageTitle(this.$route);
    if (this.$store.state.auth.status.loggedIn) {
      loadBudgetingPermissions();
      this.buildMenuPathLabelMap();
      this.updateBrowserPageTitle(this.$route);
      // TEMP_INTERNAL_TABS_OFF_START
      if (!this.isInternalTabsTemporarilyDisabled) {
        this.restoreOpenTabs();
        this.syncRouteTab(this.$route);
      }
      // TEMP_INTERNAL_TABS_OFF_END
    }
    window.addEventListener("keydown", this.onGlobalKeyDown);
    return this.$store.state.auth.user;
  },
  beforeDestroy() {
    // TEMP_INTERNAL_TABS_OFF_START
    if (!this.isInternalTabsTemporarilyDisabled) {
      unregisterTabHost(this);
    }
    // TEMP_INTERNAL_TABS_OFF_END

    window.removeEventListener("keydown", this.onGlobalKeyDown);
  },
  beforeUpdate() {
    if (this.mobileMenuActive)
      this.addClass(document.body, "body-overflow-hidden");
    else this.removeClass(document.body, "body-overflow-hidden");
  },
  components: {
    Maintenance,
    AppTopBar: AppTopBar,
    AppProfile: AppProfile,
    AppMenu: AppMenu,
    AppFooter: AppFooter,
    Notifications,
    // TEMP_INTERNAL_TABS_OFF_START
    // OpenPagesBar,
    // TEMP_INTERNAL_TABS_OFF_END
    HelpdeskQuickSearch,
  },
};
</script>
<style>
.no-margin-left {
  margin-left: 0px !important;
}

.search-input {
  width: 90%;
  margin: 10px auto;
}


.menu-search-empty {
  display: block;
  margin-top: 0.4rem;
  padding-left: 0.25rem;
  opacity: 0.75;
}

</style>
